import { useState, useEffect } from "react";

const API_BASE = window.location.href.includes('localhost') ? "http://localhost:4200/api" : "https://api-curate.pyther.com/api";

export default function ValidationHistoryPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const user = JSON.parse(localStorage.getItem("user"));
    const CURRENT_USER = {
        userId: user.userId,
        name: user.name,
        email: user.email,
        role: user.role, // "user" | "admin" | "superadmin"
    };

    const IS_ADMIN =
        CURRENT_USER.role === "admin" || CURRENT_USER.role === "superadmin";

    // Regular user state
    const [groups, setGroups] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalDays, setTotalDays] = useState(0);
    const [expandedDates, setExpandedDates] = useState({});

    // Admin state
    const [users, setUsers] = useState([]);
    const [totalValidations, setTotalValidations] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);
    const [expandedUsers, setExpandedUsers] = useState({});
    const [expandedUserDates, setExpandedUserDates] = useState({});

    useEffect(() => {
        if (IS_ADMIN) {
            fetchAllHistory();
        } else {
            fetchMyHistory();
        }
    }, []);

    const fetchMyHistory = async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${API_BASE}/validation-history/me?userId=${CURRENT_USER.userId}`
            );
            if (!res.ok) throw new Error("Failed to fetch");
            const data = await res.json();
            setGroups(data.groups);
            setTotalCount(data.totalCount);
            setTotalDays(data.totalDays);

            // Auto-expand the most recent date
            if (data.groups.length > 0) {
                setExpandedDates({ [data.groups[0]._id]: true });
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllHistory = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/validation-history/all`);
            if (!res.ok) throw new Error("Failed to fetch");
            const data = await res.json();
            setUsers(data.users);
            setTotalValidations(data.totalValidations);
            setTotalUsers(data.totalUsers);

            // Auto-expand first user
            if (data.users.length > 0) {
                setExpandedUsers({ [data.users[0]._id]: true });
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleDate = (date) =>
        setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }));

    const toggleUser = (userId) =>
        setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));

    const toggleUserDate = (userId, date) => {
        const key = `${userId}__${date}`;
        setExpandedUserDates((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    if (loading) return <div style={s.loadingWrap}>Loading history...</div>;
    if (error) return <div style={s.errorWrap}>Error: {error}</div>;

    return (
        <div style={s.page}>
            {/* ── Header ── */}
            <div style={s.headerRow}>
                <div>
                    <h1 style={s.pageTitle}>
                        {IS_ADMIN ? "All Validation History" : "My Validation History"}
                    </h1>
                    <p style={s.pageSubtitle}>
                        {IS_ADMIN
                            ? `${totalUsers} validators · ${totalValidations} total validations`
                            : `${totalCount} validations across ${totalDays} day${totalDays !== 1 ? "s" : ""}`}
                    </p>
                </div>
                {!IS_ADMIN && (
                    <div style={s.avatarCircle}>
                        {CURRENT_USER.name
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)}
                    </div>
                )}
            </div>

            {/* ── Summary stats ── */}
            {IS_ADMIN ? (
                <div style={s.statsRow}>
                    <StatCard label="Total users" value={totalUsers} color="#0C447C" />
                    <StatCard
                        label="Total validations"
                        value={totalValidations}
                        color="#1D9E75"
                    />
                    <StatCard
                        label="Avg per user"
                        value={
                            totalUsers > 0 ? Math.round(totalValidations / totalUsers) : 0
                        }
                        color="#BA7517"
                    />
                </div>
            ) : (
                <div style={s.statsRow}>
                    <StatCard label="Total validated" value={totalCount} color="#1D9E75" />
                    <StatCard label="Days active" value={totalDays} color="#0C447C" />
                    <StatCard
                        label="Avg per day"
                        value={
                            totalDays > 0 ? Math.round(totalCount / totalDays) : 0
                        }
                        color="#BA7517"
                    />
                </div>
            )}

            {/* ── Regular user view ── */}
            {!IS_ADMIN && (
                <div style={s.section}>
                    {groups.length === 0 ? (
                        <div style={s.emptyState}>
                            No validations found. Start validating records to see your history here.
                        </div>
                    ) : (
                        groups.map((group) => (
                            <DateAccordion
                                key={group._id}
                                date={group._id}
                                count={group.count}
                                entries={group.entries}
                                expanded={!!expandedDates[group._id]}
                                onToggle={() => toggleDate(group._id)}
                            />
                        ))
                    )}
                </div>
            )}

            {/* ── Admin view: User → Date ── */}
            {IS_ADMIN && (
                <div style={s.section}>
                    {users.length === 0 ? (
                        <div style={s.emptyState}>No validation history found.</div>
                    ) : (
                        users.map((u) => (
                            <div key={u._id} style={s.userBlock}>
                                {/* User header row */}
                                <div
                                    style={s.userHeader}
                                    onClick={() => toggleUser(u._id)}
                                >
                                    <div style={s.userAvatarSmall}>
                                        {u.userName
                                            ?.split(" ")
                                            .map((w) => w[0])
                                            .join("")
                                            .toUpperCase()
                                            .slice(0, 2)}
                                    </div>
                                    <div style={s.userHeaderInfo}>
                                        <div style={s.userHeaderName}>{u.userName}</div>
                                        <div style={s.userHeaderEmail}>{u.userEmail}</div>
                                    </div>
                                    <div style={s.userHeaderRight}>
                                        <span style={s.userTotalBadge}>
                                            {u.totalCount} validations
                                        </span>
                                        <span style={s.userDaysBadge}>
                                            {u.dates.length} day{u.dates.length !== 1 ? "s" : ""}
                                        </span>
                                        <span style={s.chevron}>
                                            {expandedUsers[u._id] ? "▲" : "▼"}
                                        </span>
                                    </div>
                                </div>

                                {/* Dates inside user */}
                                {expandedUsers[u._id] && (
                                    <div style={s.userDatesWrap}>
                                        {u.dates.map((d) => {
                                            const key = `${u._id}__${d.date}`;
                                            return (
                                                <DateAccordion
                                                    key={d.date}
                                                    date={d.date}
                                                    count={d.count}
                                                    entries={d.entries}
                                                    expanded={!!expandedUserDates[key]}
                                                    onToggle={() => toggleUserDate(u._id, d.date)}
                                                    nested
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// ── DateAccordion ──
function DateAccordion({ date, count, entries, expanded, onToggle, nested }) {
    const formatted = new Date(date + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <div style={{ ...s.dateBlock, ...(nested ? s.dateBlockNested : {}) }}>
            {/* Date header */}
            <div style={s.dateHeader} onClick={onToggle}>
                <div style={s.dateHeaderLeft}>
                    <div style={s.dateText}>{formatted}</div>
                    <span style={s.dateBadge}>{count} record{count !== 1 ? "s" : ""}</span>
                </div>
                <span style={s.chevron}>{expanded ? "▲" : "▼"}</span>
            </div>

            {/* Entries */}
            {expanded && (
                <div style={s.entriesList}>
                    {entries.map((e, i) => (
                        <EntryRow key={i} entry={e} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── EntryRow ──
function EntryRow({ entry }) {
    const [showSnapshot, setShowSnapshot] = useState(false);
    const time = entry.validatedAt
        ? new Date(entry.validatedAt).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
        })
        : "";

    return (
        <div style={s.entryRow}>
            <div style={s.entryTop}>
                <div style={s.entryKey}>{entry.key}</div>
                <div style={s.entryTime}>{time}</div>
            </div>
            <div style={s.entryProduct}>{entry.curatedProduct}</div>
            <div style={s.entryFields}>
                <div style={s.entryField}>
                    <span style={s.entryFieldLabel}>Final CAS</span>
                    <span style={s.entryFieldValue}>{entry.finalCas}</span>
                </div>
                <div style={s.entryField}>
                    <span style={s.entryFieldLabel}>Final UPN</span>
                    <span style={s.entryFieldValue}>{entry.finalUpn}</span>
                </div>
                {entry.note && (
                    <div style={s.entryField}>
                        <span style={s.entryFieldLabel}>Note</span>
                        <span style={{ ...s.entryFieldValue, fontStyle: "italic", color: "#666" }}>
                            {entry.note}
                        </span>
                    </div>
                )}
            </div>

            {/* Snapshot toggle */}
            {entry.snapshotRecord && (
                <div style={{ marginTop: 8 }}>
                    <button
                        style={s.snapshotToggleBtn}
                        onClick={() => setShowSnapshot((v) => !v)}
                    >
                        {showSnapshot ? "Hide" : "View"} snapshot
                    </button>
                    {showSnapshot && (
                        <div style={s.snapshotGrid}>
                            {Object.entries(entry.snapshotRecord).map(([k, v]) => (
                                <div key={k} style={s.snapshotRow}>
                                    <span style={s.snapshotKey}>{k}</span>
                                    <span style={s.snapshotVal}>
                                        {Array.isArray(v)
                                            ? v.join(", ")
                                            : v === null
                                                ? "—"
                                                : String(v)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── StatCard ──
function StatCard({ label, value, color }) {
    return (
        <div style={s.statCard}>
            <div style={{ ...s.statNum, color }}>{value}</div>
            <div style={s.statLbl}>{label}</div>
        </div>
    );
}

// ── Styles ──
const s = {
    page: {
        maxWidth: "auto",
        margin: "0 auto",
        padding: "2rem 1.25rem",
        fontFamily: "system-ui, -apple-system, sans-serif",
    },
    loadingWrap: {
        padding: "3rem",
        textAlign: "center",
        color: "#888",
        fontFamily: "system-ui, sans-serif",
    },
    errorWrap: {
        padding: "3rem",
        textAlign: "center",
        color: "#A32D2D",
        fontFamily: "system-ui, sans-serif",
    },
    headerRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        paddingTop: "5rem",
        marginBottom: "1.25rem",
    },
    pageTitle: { fontSize: 20, fontWeight: 500, color: "#111", margin: 0 },
    pageSubtitle: {
        fontSize: 13,
        color: "#888",
        marginTop: 4,
        marginBottom: 0,
    },
    avatarCircle: {
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: "#E6F1FB",
        color: "#0C447C",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 15,
        fontWeight: 500,
        flexShrink: 0,
    },
    statsRow: {
        display: "flex",
        gap: 8,
        marginBottom: "1.25rem",
    },
    statCard: {
        flex: 1,
        background: "#f7f7f7",
        borderRadius: 8,
        padding: "12px 14px",
    },
    statNum: { fontSize: 24, fontWeight: 500 },
    statLbl: { fontSize: 11, color: "#888", marginTop: 2 },
    section: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    emptyState: {
        textAlign: "center",
        color: "#aaa",
        fontSize: 14,
        padding: "3rem 1rem",
        background: "#f7f7f7",
        borderRadius: 10,
        border: "0.5px solid #eee",
    },

    // Date accordion
    dateBlock: {
        background: "#fff",
        border: "0.5px solid #ddd",
        borderRadius: 10,
        overflow: "hidden",
    },
    dateBlockNested: {
        border: "0.5px solid #eee",
        borderRadius: 8,
        marginBottom: 4,
    },
    dateHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        cursor: "pointer",
        background: "#fafafa",
        userSelect: "none",
    },
    dateHeaderLeft: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },
    dateText: {
        fontSize: 14,
        fontWeight: 500,
        color: "#111",
    },
    dateBadge: {
        fontSize: 11,
        background: "#E6F1FB",
        color: "#0C447C",
        borderRadius: 999,
        padding: "2px 10px",
        fontWeight: 500,
    },
    chevron: {
        fontSize: 10,
        color: "#aaa",
        flexShrink: 0,
    },
    entriesList: {
        display: "flex",
        flexDirection: "column",
        gap: 0,
        borderTop: "0.5px solid #eee",
    },

    // Entry row
    entryRow: {
        padding: "12px 16px",
        borderBottom: "0.5px solid #f3f3f3",
    },
    entryTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 2,
    },
    entryKey: {
        fontSize: 13,
        fontWeight: 500,
        fontFamily: "monospace",
        color: "#0C447C",
        background: "#E6F1FB",
        borderRadius: 4,
        padding: "1px 7px",
    },
    entryTime: {
        fontSize: 12,
        color: "#aaa",
    },
    entryProduct: {
        fontSize: 12,
        color: "#888",
        marginBottom: 8,
    },
    entryFields: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
    },
    entryField: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    entryFieldLabel: {
        fontSize: 10,
        color: "#aaa",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
    },
    entryFieldValue: {
        fontSize: 13,
        fontWeight: 500,
        fontFamily: "monospace",
        color: "#1D9E75",
    },

    // Snapshot
    snapshotToggleBtn: {
        fontSize: 12,
        color: "#378ADD",
        background: "none",
        border: "0.5px solid #B5D4F4",
        borderRadius: 6,
        padding: "3px 10px",
        cursor: "pointer",
    },
    snapshotGrid: {
        marginTop: 8,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        background: "#f7f7f7",
        borderRadius: 6,
        padding: "10px 12px",
        border: "0.5px solid #eee",
    },
    snapshotRow: {
        display: "flex",
        gap: 12,
        fontSize: 12,
        borderBottom: "0.5px solid #eee",
        paddingBottom: 4,
    },
    snapshotKey: {
        color: "#aaa",
        minWidth: 140,
        fontFamily: "monospace",
        flexShrink: 0,
    },
    snapshotVal: {
        color: "#444",
        fontFamily: "monospace",
        wordBreak: "break-all",
    },

    // Admin user blocks
    userBlock: {
        background: "#fff",
        border: "0.5px solid #ddd",
        borderRadius: 10,
        overflow: "hidden",
    },
    userHeader: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        cursor: "pointer",
        background: "#fafafa",
        userSelect: "none",
    },
    userAvatarSmall: {
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "#E6F1FB",
        color: "#0C447C",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 500,
        flexShrink: 0,
    },
    userHeaderInfo: { flex: 1 },
    userHeaderName: { fontSize: 14, fontWeight: 500, color: "#111" },
    userHeaderEmail: { fontSize: 12, color: "#888", marginTop: 1 },
    userHeaderRight: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
    },
    userTotalBadge: {
        fontSize: 11,
        background: "#EAF3DE",
        color: "#3B6D11",
        borderRadius: 999,
        padding: "2px 10px",
        fontWeight: 500,
    },
    userDaysBadge: {
        fontSize: 11,
        background: "#f3f3f3",
        color: "#666",
        borderRadius: 999,
        padding: "2px 10px",
    },
    userDatesWrap: {
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        borderTop: "0.5px solid #eee",
        background: "#fdfdfd",
    },
};